import type { PendingTaskKind, PendingTaskListItem } from "@closer/shared";
import {
  groupPendingTasks,
  PENDING_TASK_GROUP_NAMES,
  PENDING_TASK_KIND_NAMES,
  PENDING_TASK_KINDS,
  pendingTaskDueState,
} from "@closer/shared";
import { Badge, Button, FormField, Input, ListRow, PageHeader, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { TaskIcon } from "../../components/icons";
import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { pendingTaskPath } from "../../lib/contractRoutes";
import { resolveErrorState } from "../../lib/uiState";

/**
 * BEKLEYEN İŞLEMLER — TAM LİSTE (CLOS-181 `[163]`).
 *
 * ### ⚠ Liste BÜTÜN çalışma alanlarını kapsıyor
 *
 * Panel (`[063]`) aktif alanın işlerini gösteriyor; bu ekran kullanıcının her alandaki
 * işini bir arada veriyor. Kartın amacı bu: *"kullanıcının tüm çalışma alanlarındaki
 * gerçek workflow aksiyonları."* Bu yüzden sorgu aktif alana değil KULLANICIYA
 * anahtarlanıyor ve her satır hangi alana ait olduğunu söylüyor.
 *
 * ### ⚠ Kritik karar BURADA verilmiyor
 *
 * Satırlar aksiyon düğmesi taşımıyor; görevin DEĞİŞMEZ versiyonuna götürüyor. Listeden
 * onaylanan bir metin, okunmadan onaylanmış metindir.
 *
 * ### ⚠ Davet ve bildirim BURADA DEĞİL
 *
 * Kartın ilk kriteri. Davetler Davetlerim merkezinde, bilgilendirmeler Bildirim
 * Merkezi'nde. Bir daveti "bekleyen iş" saysaydık, kabulü bir workflow kararıymış gibi
 * görünürdü.
 */

function tarih(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR", { dateStyle: "medium" });
}

export function PendingTasksPage() {
  /** Alan bağlamında boş durum bağlantısı Müvekkil Alanı listesine gider (CLOS-1128). */
  const { memberships, activeTenant } = useAuth();
  const [turler, setTurler] = useState<PendingTaskKind[]>([]);
  const [alanlar, setAlanlar] = useState<string[]>([]);
  const [baslangic, setBaslangic] = useState("");
  const [suzgeclerAcik, setSuzgeclerAcik] = useState(false);

  const sorguDizesi = new URLSearchParams();
  if (turler.length > 0) sorguDizesi.set("kinds", turler.join(","));
  if (alanlar.length > 0) sorguDizesi.set("tenantIds", alanlar.join(","));
  if (baslangic) sorguDizesi.set("from", new Date(baslangic).toISOString());
  const sorguEki = sorguDizesi.toString();

  const sorgu = useQuery({
    queryKey: ["pending-tasks", "all", sorguEki],
    queryFn: async () =>
      (await api<PendingTaskListItem[]>(`/api/pending-tasks/all${sorguEki ? `?${sorguEki}` : ""}`))
        .data,
  });

  const hataDurumu = sorgu.error
    ? resolveErrorState(sorgu.error, {
        idempotent: true,
        existenceIsSecret: false,
        overrides: {
          error: {
            title: "Bekleyen işler yüklenemedi",
            description: "Bağlantı sorunları geçici olabilir.",
          },
        },
      })
    : null;

  const satirlar = sorgu.data ?? [];
  /**
   * ⚠ Ayrım paylaşılan katmandan geliyor (`groupPendingTasks`), ekranda yeniden
   * hesaplanmıyor: üst bar paneli ve Ana Sayfa da aynı fonksiyonu çağırıyor. Üç yüzeyin
   * kuralı ayrı ayrı yazılsaydı biri güncellenir, diğerleri unutulurdu.
   */
  const bloklar = (() => {
    const { direct, claimable } = groupPendingTasks(satirlar);
    return (
      [
        ["direct", direct],
        ["claimable", claimable],
      ] as const
    )
      .filter(([, kume]) => kume.length > 0)
      .map(([grup, kume]) => ({ grup, kume }));
  })();
  const suzgecVar = turler.length > 0 || alanlar.length > 0 || baslangic !== "";

  function suzgecleriTemizle() {
    setTurler([]);
    setAlanlar([]);
    setBaslangic("");
  }

  function turDegistir(tur: PendingTaskKind) {
    setTurler((onceki) =>
      onceki.includes(tur) ? onceki.filter((t) => t !== tur) : [...onceki, tur],
    );
  }

  function alanDegistir(tenantId: string) {
    setAlanlar((onceki) =>
      onceki.includes(tenantId) ? onceki.filter((t) => t !== tenantId) : [...onceki, tenantId],
    );
  }

  return (
    <section className="flex w-full flex-col gap-6">
      <PageHeader
        title="Bekleyen İşlemler"
        description="Bütün çalışma alanlarınızda size atanmış inceleme, onay, iş akışı aşaması, imza ve mutabakat işleri."
      />

      <Button
        type="button"
        variant="secondary"
        className="w-fit"
        aria-expanded={suzgeclerAcik}
        onClick={() => setSuzgeclerAcik((acik) => !acik)}
      >
        Süzgeçler{suzgecVar ? " (uygulanıyor)" : ""}
      </Button>

      <div
        className={`${suzgeclerAcik ? "flex" : "hidden"} flex-col gap-3 rounded-lg border border-border-default p-4`}
      >
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-fg-strong">Tür</span>
          <div className="flex flex-wrap gap-2">
            {PENDING_TASK_KINDS.map((tur) => (
              <Button
                key={tur}
                size="sm"
                variant={turler.includes(tur) ? "primary" : "secondary"}
                aria-pressed={turler.includes(tur)}
                onClick={() => turDegistir(tur)}
              >
                {PENDING_TASK_KIND_NAMES[tur]}
              </Button>
            ))}
          </div>
        </div>

        {memberships.length > 1 ? (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-fg-strong">Çalışma alanı</span>
            <div className="flex flex-wrap gap-2">
              {memberships.map((alan) => (
                <Button
                  key={alan.tenantId}
                  size="sm"
                  variant={alanlar.includes(alan.tenantId) ? "primary" : "secondary"}
                  aria-pressed={alanlar.includes(alan.tenantId)}
                  onClick={() => alanDegistir(alan.tenantId)}
                >
                  {alan.name}
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        <FormField label="Şu tarihten sonra atananlar">
          <Input
            type="date"
            value={baslangic}
            onChange={(olay) => setBaslangic(olay.target.value)}
            className="w-fit"
          />
        </FormField>
      </div>

      {sorgu.isPending ? (
        <div role="status" aria-label="Bekleyen işler yükleniyor">
          <Skeleton lines={4} />
        </div>
      ) : hataDurumu ? (
        <StateView state={hataDurumu} onRetry={() => void sorgu.refetch()} />
      ) : satirlar.length === 0 ? (
        /*
          ⚠ BOŞ DURUM İŞİN NEREDEN DOĞDUĞUNU SÖYLÜYOR (kullanıcı bildirimi, 26 Ağustos).

          Ekran süzgeç yokken bile "seçtiğiniz ölçütlere uyan iş yok" diyordu: kullanıcı
          hiçbir ölçüt seçmemişti ve cümle olmayan bir süzgeci suçluyordu. Dahası iş atamanın
          NEREDE yapıldığını hiç söylemiyordu. İşi bu ekran doğurmuyor — sözleşme detayındaki
          akış başlatma adımları doğuruyor; boş durum artık oraya yol gösteriyor.
        */
        <div className="flex flex-col items-start gap-2 rounded-lg border border-border-default p-4">
          {suzgecVar ? (
            <>
              <p className="text-sm text-fg-muted">Seçtiğiniz ölçütlere uyan bekleyen iş yok.</p>
              <Button size="sm" variant="secondary" onClick={suzgecleriTemizle}>
                Süzgeçleri temizle
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-fg-strong">Size atanmış bekleyen iş yok.</p>
              <p className="text-sm text-fg-muted">
                İşler burada değil, sözleşmenin kendi sayfasında doğuyor: bir sözleşmeyi açıp{" "}
                <span className="text-fg-strong">İnceleme başlat</span>,{" "}
                <span className="text-fg-strong">Onay akışı başlat</span>,{" "}
                <span className="text-fg-strong">İmzacıları belirle</span> ya da{" "}
                <span className="text-fg-strong">Mutabakat başlat</span> adımlarından biriyle
                kişileri görevlendirdiğinizde, o kişilerin listesinde görünür.
              </p>
              <Link
                className="text-sm font-medium text-primary hover:underline"
                to={
                  activeTenant?.type === "client_workspace"
                    ? "/app/muvekkil-alani"
                    : "/app/sozlesmeler"
                }
              >
                Sözleşmelere git
              </Link>
            </>
          )}
        </div>
      ) : (
        /*
          ⚠ İKİ SEMANTİK GRUP (CLOS-1243 — kanon §7, §28).

          "Benden Beklenenler" zorunlu kişisel işler, "Üstlenebileceğim" havuzdan
          üstlenilebilecek işler. Tek listede gösterseydik kullanıcı hangisinin kendisinden
          beklendiğini ayırt edemez, üst bardaki rozet de listeyle çelişirdi.
        */
        bloklar.map(({ grup, kume }) => (
          <section key={grup} className="flex flex-col gap-2">
            {bloklar.length > 1 ? (
              <h2 className="text-sm font-medium text-fg-strong" data-testid={`liste-grup-${grup}`}>
                {PENDING_TASK_GROUP_NAMES[grup]}
              </h2>
            ) : null}
            <ul className="flex flex-col gap-2">
              {kume.map((gorev) => (
                <li key={gorev.id}>
                  {/* ⚠ Hedef görevin DEĞİŞMEZ versiyonu; karar okunan metin üzerinde verilir. */}
                  <Link to={pendingTaskPath(gorev)} className="contents">
                    <ListRow
                      icon={<TaskIcon className="size-[18px]" />}
                      title={gorev.contractTitle}
                      /* ⚠ Son tarih künyeye giriyor (CLOS-1101): gecikmiş görev artık listede
                     duruyor ve NEDEN öne çıktığı okunabilir olmalı. */
                      subtitle={[
                        gorev.tenantName,
                        tarih(gorev.assignedAt),
                        pendingTaskDueState(gorev.dueAt, new Date()).label,
                        /* ⚠ Talep eden künyede (CLOS-1112, R5 §12.3): işin KİMDEN geldiği
                       görünmüyordu. Başlatan bilinmiyorsa etiket boş bırakılmaz, hiç çizilmez. */
                        gorev.requestedByName ? `Talep eden: ${gorev.requestedByName}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                      trailing={
                        <span className="flex items-center gap-1.5">
                          {/* ⚠ Gecikme AYRI bir rozet: tür rozetiyle aynı yere sıkıştırılsaydı
                          "hangi iş" ile "ne durumda" tek kelimeye inerdi. */}
                          {pendingTaskDueState(gorev.dueAt, new Date()).overdue ? (
                            <Badge tone="danger">Gecikti</Badge>
                          ) : null}
                          <Badge tone="warning">{PENDING_TASK_KIND_NAMES[gorev.kind]}</Badge>
                        </span>
                      }
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </section>
  );
}
